/**
 * WebLLM Service for Client-Side LLM Inference
 * 
 * Manages TinyLlama (1.1B) model via WebLLM for:
 * - Policy explanation
 * - Natural language to policy translation
 * - Threat analysis
 * - Command translation
 * 
 * Privacy-First: All inference runs locally in WebGPU.
 * 
 * Peer Review Fixes Applied:
 * - Switched from Phi-3 (3.8B) to TinyLlama (1.1B) for <2GB VRAM (Gemini 6/10)
 * - Added storage quota check before model download
 * - Added model integrity verification (planned)
 * 
 * Peer Review: Gemini 3 Pro, GPT-4o, GPT-5.1 (2025-12-07)
 */

import { CreateWebWorkerMLCEngine, MLCEngineInterface, InitProgressReport } from '@mlc-ai/web-llm';

// ============================================================================
// Types
// ============================================================================

export interface WebLLMConfig {
  modelId: string;
  onProgress?: (progress: InitProgressReport) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
}

export interface InferenceOptions {
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  systemPrompt?: string;
  stopSequences?: string[];
}

export interface InferenceResult {
  text: string;
  tokensUsed: number;
  finishReason: 'stop' | 'length' | 'error';
  latencyMs: number;
}

// ============================================================================
// Constants
// ============================================================================

// Supported models (must be in WebLLM model library)
export const SUPPORTED_MODELS = {
  PHI3_MINI: 'Phi-3-mini-4k-instruct-q4f16_1-MLC',
  PHI3_MINI_FP16: 'Phi-3-mini-4k-instruct-q4f32_1-MLC',
  SMOL_LM: 'SmolLM-1.7B-Instruct-q4f16_1-MLC',
  TINYLLAMA: 'TinyLlama-1.1B-Chat-v0.4-q4f16_1-MLC',
} as const;

// System prompts for different use cases
export const SYSTEM_PROMPTS = {
  POLICY_EXPLANATION: `You are an expert security analyst explaining policies to IT administrators. 
Be concise, accurate, and focus on practical implications. 
Use bullet points for clarity. 
Reference specific policy rules when relevant.`,

  THREAT_ANALYSIS: `You are a security threat analyst. 
Analyze the given threat or log entry and provide:
1. Threat classification
2. Severity assessment
3. Recommended actions
4. Potential impact
Be factual and avoid speculation.`,

  COMMAND_TRANSLATION: `You are a cross-platform command translator.
Convert the given command or natural language description to the appropriate command for the target system.
Support: Windows (PowerShell/CMD), Linux (Bash), macOS, and common network devices.
Provide the exact command with explanations for complex flags.`,

  POLICY_GENERATION: `You are a security policy generator.
Convert natural language security requirements into formal policy rules.
Output should be in a structured format that can be parsed programmatically.
Include: conditions, actions, scope, and priority.`,
};

// Default inference options
const DEFAULT_OPTIONS: InferenceOptions = {
  maxTokens: 1024,
  temperature: 0.7,
  topP: 0.9,
  systemPrompt: '',
  stopSequences: [],
};

// ============================================================================
// WebLLM Service Class
// ============================================================================

// Model sizes for storage quota check (approximate compressed sizes in bytes)
const MODEL_SIZES: Record<string, number> = {
  [SUPPORTED_MODELS.TINYLLAMA]: 700_000_000, // ~700MB (recommended for <2GB VRAM)
  [SUPPORTED_MODELS.SMOL_LM]: 1_200_000_000, // ~1.2GB
  [SUPPORTED_MODELS.PHI3_MINI]: 2_500_000_000, // ~2.5GB (requires >4GB VRAM)
  [SUPPORTED_MODELS.PHI3_MINI_FP16]: 4_000_000_000, // ~4GB (not recommended)
};

// Default model: TinyLlama for <2GB VRAM (per peer review)
export const DEFAULT_MODEL = SUPPORTED_MODELS.TINYLLAMA;

// Singleton instance holder (peer review fix: prevent OOM from multiple instances)
let webLLMInstance: WebLLMService | null = null;

// Default inference timeout in milliseconds (peer review fix: prevent hanging)
const DEFAULT_INFERENCE_TIMEOUT_MS = 60_000; // 60 seconds

export class WebLLMService {
  private engine: MLCEngineInterface | null = null;
  private worker: Worker | null = null;
  private modelId: string = DEFAULT_MODEL; // Changed from PHI3_MINI per peer review
  private isLoading = false;
  private isReady = false;
  
  // Concurrency control (peer review fix: prevent race conditions)
  private inferenceLock = false;
  private inferenceAbortController: AbortController | null = null;

  // Event callbacks
  private onProgress: ((progress: InitProgressReport) => void) | null = null;
  private onReady: (() => void) | null = null;
  private onError: ((error: Error) => void) | null = null;

  // ============================================================================
  // Singleton Pattern (peer review fix: prevent GPU resource exhaustion)
  // ============================================================================

  /**
   * Get the singleton instance of WebLLMService
   * Enforces single GPU context to prevent OOM crashes
   */
  static getInstance(): WebLLMService {
    if (!webLLMInstance) {
      webLLMInstance = new WebLLMService();
    }
    return webLLMInstance;
  }

  /**
   * Reset singleton (use with caution - for testing only)
   */
  static resetInstance(): void {
    if (webLLMInstance) {
      webLLMInstance.unload().catch(console.error);
      webLLMInstance = null;
    }
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  /**
   * Check if there's enough storage for the model (peer review fix)
   */
  async checkStorageQuota(modelId: string): Promise<{ hasSpace: boolean; available: number; required: number }> {
    const required = MODEL_SIZES[modelId] ?? 1_000_000_000;
    
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const available = (estimate.quota ?? 0) - (estimate.usage ?? 0);
        return {
          hasSpace: available >= required * 1.2, // 20% buffer for safety
          available,
          required,
        };
      }
    } catch {
      // Storage API not available, assume we have space
    }
    
    return { hasSpace: true, available: 0, required };
  }

  /**
   * Initialize the WebLLM engine with a specific model
   */
  async initialize(config: WebLLMConfig): Promise<void> {
    if (this.isLoading) {
      throw new Error('WebLLM is already loading');
    }

    if (this.isReady && this.modelId === config.modelId) {
      return; // Already initialized with same model
    }

    this.modelId = config.modelId;
    this.onProgress = config.onProgress ?? null;
    this.onReady = config.onReady ?? null;
    this.onError = config.onError ?? null;
    this.isLoading = true;

    try {
      // Check WebGPU availability
      if (!this.checkWebGPU()) {
        throw new Error('WebGPU is not available in this browser. WebLLM requires WebGPU for efficient inference.');
      }

      // Check storage quota before downloading (peer review fix: PEER-FIX-8)
      const quota = await this.checkStorageQuota(this.modelId);
      if (!quota.hasSpace) {
        const requiredGB = (quota.required / 1_000_000_000).toFixed(1);
        const availableGB = (quota.available / 1_000_000_000).toFixed(1);
        throw new Error(
          `Insufficient storage for AI model. Required: ${requiredGB}GB, Available: ${availableGB}GB. ` +
          `Please free up space or use a smaller model.`
        );
      }

      // Create WebWorker for LLM engine (keeps main thread responsive)
      this.worker = new Worker(
        new URL('../../workers/llm.worker.ts', import.meta.url),
        { type: 'module' }
      );

      // Create engine via worker proxy
      this.engine = await CreateWebWorkerMLCEngine(
        this.worker,
        this.modelId,
        {
          initProgressCallback: (report) => {
            this.onProgress?.(report);
          },
        }
      );

      this.isReady = true;
      this.isLoading = false;
      this.onReady?.();

    } catch (error) {
      this.isLoading = false;
      const err = error instanceof Error ? error : new Error('Failed to initialize WebLLM');
      this.onError?.(err);
      throw err;
    }
  }

  /**
   * Check if WebGPU is available
   */
  private checkWebGPU(): boolean {
    return 'gpu' in navigator;
  }

  // ============================================================================
  // Inference Methods
  // ============================================================================

  /**
   * Run inference with the loaded model
   * 
   * Peer review fixes applied:
   * - Inference lock prevents concurrent requests (race condition fix)
   * - Timeout prevents hanging on model freeze
   * - Proper abort handling
   */
  async inference(
    prompt: string,
    options: InferenceOptions = {}
  ): Promise<InferenceResult> {
    if (!this.engine || !this.isReady) {
      throw new Error('WebLLM engine not initialized. Call initialize() first.');
    }

    // Peer review fix: Prevent concurrent inference (race condition)
    if (this.inferenceLock) {
      throw new Error('Inference already in progress. Wait for completion or call abort().');
    }

    this.inferenceLock = true;
    this.inferenceAbortController = new AbortController();
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const startTime = performance.now();

    try {
      const messages: { role: 'system' | 'user' | 'assistant'; content: string }[] = [];

      // Add system prompt if provided
      if (opts.systemPrompt) {
        messages.push({ role: 'system', content: opts.systemPrompt });
      }

      // Add user prompt
      messages.push({ role: 'user', content: prompt });

      // Peer review fix: Add timeout to prevent hanging
      const completionPromise = this.engine.chat.completions.create({
        messages,
        max_tokens: opts.maxTokens ?? 1024,
        temperature: opts.temperature ?? 0.7,
        top_p: opts.topP ?? 0.9,
        stop: opts.stopSequences ?? [],
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error(`Inference timed out after ${DEFAULT_INFERENCE_TIMEOUT_MS / 1000}s`));
        }, DEFAULT_INFERENCE_TIMEOUT_MS);
        
        // Clear timeout if abort is called
        this.inferenceAbortController?.signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new Error('Inference aborted by user'));
        });
      });

      const response = await Promise.race([completionPromise, timeoutPromise]);

      const latencyMs = performance.now() - startTime;
      const choice = response.choices[0];

      return {
        text: choice?.message.content ?? '',
        tokensUsed: response.usage?.total_tokens ?? 0,
        finishReason: choice?.finish_reason === 'stop' ? 'stop' : 
                      choice?.finish_reason === 'length' ? 'length' : 'error',
        latencyMs,
      };

    } catch (error) {
      const latencyMs = performance.now() - startTime;
      this.onError?.(error instanceof Error ? error : new Error('Inference failed'));
      
      return {
        text: '',
        tokensUsed: 0,
        finishReason: 'error',
        latencyMs,
      };
    } finally {
      // Always release the lock (peer review fix: prevent deadlock)
      this.inferenceLock = false;
      this.inferenceAbortController = null;
    }
  }

  /**
   * Abort the current inference (peer review addition)
   */
  abortInference(): void {
    if (this.inferenceAbortController) {
      this.inferenceAbortController.abort();
    }
  }

  /**
   * Check if inference is currently running
   */
  get isInferring(): boolean {
    return this.inferenceLock;
  }

  /**
   * Stream inference for longer responses
   */
  async *inferenceStream(
    prompt: string,
    options: InferenceOptions = {}
  ): AsyncGenerator<string, InferenceResult, undefined> {
    if (!this.engine || !this.isReady) {
      throw new Error('WebLLM engine not initialized. Call initialize() first.');
    }

    const opts = { ...DEFAULT_OPTIONS, ...options };
    const startTime = performance.now();
    let totalText = '';
    let tokensUsed = 0;

    try {
      const messages: { role: 'system' | 'user' | 'assistant'; content: string }[] = [];

      if (opts.systemPrompt) {
        messages.push({ role: 'system', content: opts.systemPrompt });
      }
      messages.push({ role: 'user', content: prompt });

      const stream = await this.engine.chat.completions.create({
        messages,
        max_tokens: opts.maxTokens ?? 1024,
        temperature: opts.temperature ?? 0.7,
        top_p: opts.topP ?? 0.9,
        stop: opts.stopSequences ?? [],
        stream: true,
      } as const);

      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta?.content ?? '';
        totalText += delta;
        tokensUsed = chunk.usage?.total_tokens ?? tokensUsed;
        yield delta;
      }

      return {
        text: totalText,
        tokensUsed,
        finishReason: 'stop',
        latencyMs: performance.now() - startTime,
      };

    } catch (error) {
      this.onError?.(error instanceof Error ? error : new Error('Stream inference failed'));
      return {
        text: totalText,
        tokensUsed,
        finishReason: 'error',
        latencyMs: performance.now() - startTime,
      };
    }
  }

  // ============================================================================
  // Simple Generation Method (for PolicyTranslator compatibility)
  // ============================================================================

  /**
   * Simple text generation with options
   */
  async generate(
    prompt: string,
    options?: { maxTokens?: number; temperature?: number; topP?: number }
  ): Promise<string> {
    const inferenceOpts: InferenceOptions = {};
    if (options?.maxTokens !== undefined) {inferenceOpts.maxTokens = options.maxTokens;}
    if (options?.temperature !== undefined) {inferenceOpts.temperature = options.temperature;}
    if (options?.topP !== undefined) {inferenceOpts.topP = options.topP;}
    
    const result = await this.inference(prompt, inferenceOpts);
    return result.text;
  }

  // ============================================================================
  // Use Case Methods
  // ============================================================================

  /**
   * Explain a security policy in plain language
   */
  async explainPolicy(policyJson: string): Promise<InferenceResult> {
    const prompt = `Explain the following security policy in plain language:

${policyJson}

Provide:
1. What this policy does
2. Who is affected
3. What actions are taken
4. Any potential issues to be aware of`;

    return this.inference(prompt, {
      systemPrompt: SYSTEM_PROMPTS.POLICY_EXPLANATION,
      maxTokens: 512,
      temperature: 0.5,
    });
  }

  /**
   * Analyze a threat or security event
   */
  async analyzeThreat(threatDescription: string): Promise<InferenceResult> {
    const prompt = `Analyze this security threat/event:

${threatDescription}

Provide your analysis following the structured format.`;

    return this.inference(prompt, {
      systemPrompt: SYSTEM_PROMPTS.THREAT_ANALYSIS,
      maxTokens: 768,
      temperature: 0.3,
    });
  }

  /**
   * Translate natural language to a command
   */
  async translateCommand(
    naturalLanguage: string,
    targetPlatform: 'windows' | 'linux' | 'macos' | 'network'
  ): Promise<InferenceResult> {
    const prompt = `Target platform: ${targetPlatform}

User request: "${naturalLanguage}"

Provide the exact command(s) to accomplish this task.`;

    return this.inference(prompt, {
      systemPrompt: SYSTEM_PROMPTS.COMMAND_TRANSLATION,
      maxTokens: 256,
      temperature: 0.2,
    });
  }

  /**
   * Generate policy from natural language description
   */
  async generatePolicy(requirements: string): Promise<InferenceResult> {
    const prompt = `Security requirements:

${requirements}

Generate a formal security policy with conditions, actions, and scope.`;

    return this.inference(prompt, {
      systemPrompt: SYSTEM_PROMPTS.POLICY_GENERATION,
      maxTokens: 1024,
      temperature: 0.4,
    });
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  /**
   * Unload the model and free GPU memory
   */
  async unload(): Promise<void> {
    if (this.engine) {
      await this.engine.unload();
      this.engine = null;
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    this.isReady = false;
  }

  /**
   * Check if the service is ready for inference
   */
  get ready(): boolean {
    return this.isReady;
  }

  /**
   * Check if the service is currently loading
   */
  get loading(): boolean {
    return this.isLoading;
  }

  /**
   * Get the currently loaded model ID
   */
  get currentModel(): string {
    return this.modelId;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let webLLMServiceInstance: WebLLMService | null = null;

export function getWebLLMService(): WebLLMService {
  if (!webLLMServiceInstance) {
    webLLMServiceInstance = new WebLLMService();
  }
  return webLLMServiceInstance;
}

export async function initializeWebLLM(
  config: WebLLMConfig
): Promise<WebLLMService> {
  const service = getWebLLMService();
  await service.initialize(config);
  return service;
}

// Export for type usage
export type WebLLMModel = typeof SUPPORTED_MODELS[keyof typeof SUPPORTED_MODELS];
export type GenerationOptions = InferenceOptions;

export const webLLMService = getWebLLMService();
export default webLLMService;

